import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { FilterTemplateValueToken } from '@sleed/bizcommerce-api';
import { filterTemplateValuesApi } from '~/lib/api/client';

export const filterTemplateValueKeys = {
	all: ['filter-template-values'] as const,
	byTemplate: (id: number) => [...filterTemplateValueKeys.all, 'by-template', id] as const,
	available: (id: number) => [...filterTemplateValueKeys.all, 'available', id] as const
};

/** Filter values currently attached to a template. */
export function useFilterTemplateValues(templateId: number | null) {
	return useQuery<FilterTemplateValueToken[]>({
		queryKey: filterTemplateValueKeys.byTemplate(templateId ?? 0),
		queryFn: async () => (await filterTemplateValuesApi.getByFilterTemplate(templateId!)).data.items ?? [],
		enabled: templateId != null && templateId > 0
	});
}

/** Filters available to add to a template (not yet attached). Only fetched when the add dialog opens. */
export function useAvailableFilters(templateId: number | null, enabled: boolean) {
	return useQuery<FilterTemplateValueToken[]>({
		queryKey: filterTemplateValueKeys.available(templateId ?? 0),
		queryFn: async () => (await filterTemplateValuesApi.getAvailableFiltersByFilterTemplate(templateId!)).data.items ?? [],
		enabled: enabled && templateId != null && templateId > 0
	});
}

export function useFilterTemplateValueMutations(templateId: number | null) {
	const qc = useQueryClient();
	const invalidate = () => {
		qc.invalidateQueries({ queryKey: filterTemplateValueKeys.byTemplate(templateId ?? 0) });
		qc.invalidateQueries({ queryKey: filterTemplateValueKeys.available(templateId ?? 0) });
	};
	const addBulk = useMutation({
		mutationFn: async (tokens: FilterTemplateValueToken[]) => (await filterTemplateValuesApi.insertBulk(tokens)).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (token: FilterTemplateValueToken) => (await filterTemplateValuesApi.update(token)).data,
		onSuccess: invalidate
	});
	const remove = useMutation({
		mutationFn: async (id: number) => (await filterTemplateValuesApi.remove(id)).data,
		onSuccess: invalidate
	});
	// Saves the storefront order (`si`) of the given rows. The cached list takes the new order at once,
	// so a dragged row does not snap back while the request runs, and returns to the old order on error.
	const reorder = useMutation({
		mutationFn: async (tokens: FilterTemplateValueToken[]) => (await filterTemplateValuesApi.updateBulk(tokens)).data,
		onMutate: async (tokens: FilterTemplateValueToken[]) => {
			const key = filterTemplateValueKeys.byTemplate(templateId ?? 0);
			await qc.cancelQueries({ queryKey: key });
			const previous = qc.getQueryData<FilterTemplateValueToken[]>(key);
			const nextSi = new Map(tokens.map(token => [token.id, token.si]));
			qc.setQueryData<FilterTemplateValueToken[]>(key, rows =>
				rows?.map(row => (nextSi.has(row.id) ? { ...row, si: nextSi.get(row.id) } : row))
			);
			return { previous };
		},
		onError: (_err, _tokens, context) => {
			if (context?.previous) qc.setQueryData(filterTemplateValueKeys.byTemplate(templateId ?? 0), context.previous);
		},
		onSettled: invalidate
	});
	return { addBulk, update, remove, reorder };
}
